import React, { useState } from "react";
import { Modal, Form, Input, Button } from "antd";
import { motion } from "framer-motion";
import { FaUser, FaEnvelope, FaPhone } from "react-icons/fa";
import { useLanguage } from "../../context/LanguageContext";

export default function GuestBookingModal({
  open,
  onCancel,
  onSubmit,
  loading = false,
}) {
  const { t } = useLanguage();
  const [form] = Form.useForm();

  const handleSubmit = async (values) => {
    try {
      await onSubmit(values);
      form.resetFields();
    } catch (error) {
      // handled in parent
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={handleCancel}
      footer={null}
      closable={false}
      centered
      maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}
      bodyStyle={{
        borderRadius: "12px",
        padding: "24px",
      }}
      className="rounded-xl p-0"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="flex flex-col"
      >
        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="mb-4 rounded-full bg-goldy p-4 shadow-md mx-auto w-fit"
          >
            <FaUser className="text-3xl text-white" />
          </motion.div>

          <h2 className="mb-2 text-2xl font-bold text-textPrimary">
            {t("guest_booking.title")}
          </h2>
          <p className="text-sm text-placeHolder leading-relaxed">
            {t("guest_booking.subtitle")}
          </p>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          className="space-y-4"
        >
          {/* Full Name */}
          <Form.Item
            name="fullName"
            label={
              <span className="flex items-center gap-2 text-textPrimary font-medium">
                <FaUser className="text-primary" size={14} />
                {t("guest_booking.full_name")}
              </span>
            }
            rules={[
              { required: true, message: t("guest_booking.errors.name_required") },
              { min: 2, message: t("guest_booking.errors.name_short") },
            ]}
          >
            <Input
              placeholder={t("guest_booking.placeholders.name")}
              className="rounded-lg border border-placeHolder focus:border-primary h-10"
              disabled={loading}
            />
          </Form.Item>

          {/* Email (Optional) */}
          <Form.Item
            tooltip={t("guest_booking.email_tooltip")}
            name="email"
            label={
              <span className="flex items-center gap-2 text-textPrimary font-medium">
                <FaEnvelope className="text-primary" size={14} />
                {t("guest_booking.email_label")}{" "}
                <span className="text-primary/90">
                  ({t("guest_booking.optional")})
                </span>
              </span>
            }
          >
            <Input
              placeholder={t("guest_booking.placeholders.email")}
              className="rounded-lg border border-placeHolder focus:border-primary h-10"
              disabled={loading}
            />
            <span className="text-xs text-secondary_hover">
              {t("guest_booking.email_note")}
            </span>
          </Form.Item>

          {/* Phone Number */}
          <Form.Item
            name="phoneNumber"
            label={
              <span className="flex items-center gap-2 text-textPrimary font-medium mt-1">
                <FaPhone className="text-primary" size={14} />
                {t("guest_booking.phone")}
              </span>
            }
            rules={[
              { required: true, message: t("guest_booking.errors.phone_required") },
              {
                pattern: /^[+]?[\d\s\-\(\)]{10,}$/,
                message: t("guest_booking.errors.phone_invalid"),
              },
            ]}
          >
            <Input
              placeholder={t("guest_booking.placeholders.phone")}
              className="rounded-lg border border-placeHolder focus:border-primary h-10"
              disabled={loading}
            />
          </Form.Item>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleCancel}
              className="flex-1 rounded-lg border border-placeHolder bg-transparent text-placeHolder hover:bg-white h-10"
              disabled={loading}
            >
              {t("guest_booking.cancel")}
            </Button>
            <Button
              type=""
              htmlType="submit"
              loading={loading}
              className="flex-1 bg-goldy text-white rounded-lg h-10 font-semibold"
            >
              {t("guest_booking.continue")}
            </Button>
          </div>
        </Form>
      </motion.div>
    </Modal>
  );
}
